import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Send, Loader2 } from 'lucide-react';
import AgenteIcono from './AgenteIcono';
import MicButton from './MicButton';
import { PropuestasDelAgente, Descargas } from '../pages/AgentePage';

/**
 * Agente IA desde el buscador global (2026-07-25): para TODO el personal,
 * cada uno con sus permisos. Conversación efímera (no se guarda historial
 * en el servidor); las citas propuestas se confirman con las APIs de citas
 * de siempre.
 */
export default function AgenteAskDialog({ open, onOpenChange, initialQuery = '', onCitaCambiada }) {
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [pensando, setPensando] = useState(false);
  const [procesandoId, setProcesandoId] = useState(null);
  const finRef = useRef(null);
  const lanzadoRef = useRef(false);

  const enviar = async (fuerza) => {
    const t = (fuerza ?? texto).trim();
    if (!t || pensando) return;
    const nuevo = { id: `u-${Date.now()}`, role: 'user', texto: t };
    const historial = [...mensajes, nuevo];
    setMensajes(historial);
    setTexto('');
    setPensando(true);
    try {
      // El servidor contesta al momento con un id de trabajo y sigue por
      // detrás; aquí se pregunta por él hasta que esté (2026-08-17).
      const r = await api.post('/api/agente/preguntar', {
        messages: historial.map((m) => ({ role: m.role, texto: m.texto })),
      });
      const trabajoId = r.data?.trabajo_id;
      if (!trabajoId) throw new Error('sin trabajo');

      const LIMITE = 120;   // ~3 min: pasado eso, algo va mal de verdad
      let hecho = null;
      for (let i = 0; i < LIMITE && !hecho; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => setTimeout(res, i < 6 ? 800 : 1500));
        // eslint-disable-next-line no-await-in-loop
        const e = await api.get(`/api/agente/trabajo/${trabajoId}`);
        if (e.data?.estado !== 'trabajando') hecho = e.data;
      }
      if (!hecho) throw new Error('el Agente tardó demasiado');
      if (hecho.estado === 'error') throw new Error(hecho.texto || 'El Agente no pudo responder');

      setMensajes((prev) => [...prev, {
        id: `a-${Date.now()}`,
        role: 'agente',
        texto: hecho.texto || '',
        descargas: hecho.descargas || [],
        propuestas: hecho.propuestas || [],
        propuesta_id: hecho.propuesta_id || null,
      }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'El Agente no pudo responder');
    } finally {
      setPensando(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setMensajes([]); setTexto(''); lanzadoRef.current = false;
      return;
    }
    const q = (initialQuery || '').trim();
    if (q && !lanzadoRef.current) {
      lanzadoRef.current = true;
      enviar(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialQuery]);

  useEffect(() => {
    const id = setTimeout(() => finRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
    return () => clearTimeout(id);
  }, [mensajes, pensando]);

  // Lo ejecuta el SERVIDOR (2026-07-27). La conversación aquí es efímera, pero
  // la propuesta se guardó al generarse y viene con su `propuesta_id`.
  const estadoCita = async (m, indice, estado) => {
    if (!m.propuesta_id) return;
    setProcesandoId(`${m.id}:${indice}`);
    try {
      const r = await api.post(`/api/agente/propuesta-accion/${m.propuesta_id}/${indice}/${estado}`);
      if (estado === 'confirmada') {
        toast.success(r.data?.mensaje || 'Hecho');
        // Abierto desde la agenda: que la cita aparezca en el cuadrante sin
        // tener que recargar la página.
        onCitaCambiada?.();
      }
      setMensajes((prev) => prev.map((x) => (x.id === m.id ? {
        ...x,
        propuestas: x.propuestas.map((p, i) => (i === indice ? { ...p, estado } : p)),
      } : x)));
    } catch (e) {
      toast.error(e?.response?.status === 409
        ? (e?.response?.data?.detail || 'Ese hueco ya no está libre — pídele otro al Agente')
        : e?.response?.data?.detail || 'No se pudo completar la gestión');
    } finally {
      setProcesandoId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg flex flex-col max-h-[85dvh]"
        onOpenAutoFocus={(e) => {
          // En móvil/tablet el autofoco del diálogo levantaba el teclado nada
          // más abrir y tapaba toda la ventana (2026-08-23, dirección): el
          // teclado sale solo al TOCAR el cuadro de escribir. En ordenador se
          // conserva el autofoco, que ahí sí ayuda.
          if (window.matchMedia?.('(pointer: coarse)').matches) e.preventDefault();
        }}
        overlayClassName="bg-black/55 backdrop-blur-[2px]"
        data-testid="agente-ask-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <AgenteIcono size={36} />
            Agente IA
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3 pr-1 min-h-[8rem]">
          {mensajes.map((m) => (m.role === 'user' ? (
            <div key={m.id}
              className="burbuja-usuario mensaje-entra ml-auto max-w-[85%] rounded-2xl rounded-br-md text-white px-3.5 py-2 text-sm whitespace-pre-wrap break-words">
              {m.texto}
            </div>
          ) : (
            <div key={m.id}
              className="burbuja-agente mensaje-entra max-w-[95%] rounded-2xl rounded-bl-md px-3.5 py-2 text-sm whitespace-pre-wrap break-words">
              {m.texto}
              <Descargas descargas={m.descargas} />
              <PropuestasDelAgente m={m} onEstado={estadoCita} procesandoIdx={procesandoId} />
            </div>
          )))}
          {pensando && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1" aria-hidden>
                {[0, 1, 2].map((i) => (
                  <span key={i} className="punto-pensando inline-block w-1.5 h-1.5 rounded-full bg-[#2E6E5E]"
                    style={{ animationDelay: `${i * 160}ms` }} />
                ))}
              </span>
              Pensando…
            </div>
          )}
          <div ref={finRef} />
        </div>
        <div className="flex gap-2 pt-2 border-t border-border">
          <Input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') enviar(); }}
            placeholder="Pide lo que necesites…"
            data-testid="agente-ask-input"
          />
          {/* Dictado: lo tenía la IA de agenda y en recepción se usa para pedir
              la cita hablando. Se conserva al unificar los chats (2026-07-26). */}
          <MicButton onText={(t) => setTexto((prev) => (prev ? `${prev} ${t}` : t))} />
          <Button onClick={() => enviar()} disabled={pensando || !texto.trim()}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="agente-ask-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
